// Interaktywny flow paragonu (moneyback, bez logowania).
//
// Wejście: submit głównego formularza zgłoszeniowego (.application-form). Zamiast wysyłać formularz
// klasycznie, pokazujemy modal „Odczytujemy dane…", tworzymy zgłoszenie (POST /api/application),
// a następnie sterujemy modalami odczytu (Sprawdź dane → AKCEPTUJĘ / ręczna korekta → Dziękujemy).
// Paragon jest odczytywany w tle przez assets/js/form/receipt.js przy wyborze pliku (fake kroki
// ładowania + realny scan → receiptUuid), więc tutaj korzystamy już z gotowego receiptUuid.

const ENDPOINTS = {
    application: '/api/application',
    modalResult: (uuid) => `/api/receipt/modal/result?uuid=${encodeURIComponent(uuid)}`,
    modalManualWarning: (uuid) => `/api/receipt/modal/manual-warning?uuid=${encodeURIComponent(uuid)}`,
    modalManualForm: (uuid) => `/api/receipt/modal/manual-form?uuid=${encodeURIComponent(uuid)}`,
    modalHelp: '/api/receipt/modal/help',
    modalError: '/api/receipt/modal/error',
    modalScanError: (variant) => `/api/receipt/modal/error?variant=${encodeURIComponent(variant)}`,
    modalLimit: '/api/receipt/modal/limit',
    accept: (uuid) => `/api/receipt/${uuid}/accept`,
    manual: (uuid) => `/api/receipt/${uuid}/manual`,
    cancel: (uuid) => `/api/receipt/${uuid}/cancel`,
};

const ROSSMANN_NIP = '7270019183';

const LOADER_RING_SVG = `
<svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
        <linearGradient id="paragon-loader-grad-js" x1="40" y1="0" x2="40" y2="80" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="#cfa34f" stop-opacity="0"/>
            <stop offset="1" stop-color="#cfa34f"/>
        </linearGradient>
    </defs>
    <path fill-rule="evenodd" clip-rule="evenodd"
          d="M40 80C62.0914 80 80 62.0914 80 40C80 17.9086 62.0914 0 40 0C17.9086 0 0 17.9086 0 40C0 62.0914 17.9086 80 40 80ZM40 73.3333C58.4095 73.3333 73.3333 58.4095 73.3333 40C73.3333 21.5905 58.4095 6.66667 40 6.66667C21.5905 6.66667 6.66667 21.5905 6.66667 40C6.66667 58.4095 21.5905 73.3333 40 73.3333Z"
          fill="url(#paragon-loader-grad-js)"/>
</svg>`;

export function initParagonUpload() {
    const form = document.querySelector('.application-form');
    const modal = document.querySelector('[data-paragon-modal]');
    const content = modal?.querySelector('[data-paragon-modal-content]');
    if (!form || !modal || !content) return;

    let currentUuid = null;
    let progressTimer = null;
    let progressValue = 0;

    const open = () => { modal.hidden = false; modal.classList.add('is-open'); document.body.classList.add('paragon-modal-open'); };
    const close = () => { modal.classList.remove('is-open'); modal.hidden = true; document.body.classList.remove('paragon-modal-open'); };

    const setStep = (html) => {
        content.innerHTML = html;
        const uuidEl = content.querySelector('[data-paragon-uuid]');
        if (uuidEl) currentUuid = uuidEl.dataset.paragonUuid;
        bindStepHandlers();
    };

    // Statyczny fallback: gdy padnie nawet endpoint modala błędu, user musi zobaczyć komunikat,
    // a nie pusty biały prostokąt (setStep('') czyścił zawartość modala).
    const FALLBACK_ERROR_HTML = `
        <div class="paragon-modal-step paragon-modal-step--error" data-step="error">
            <h2 class="paragon-modal__title">Coś poszło nie tak...</h2>
            <p class="paragon-modal__subtitle">Odśwież stronę i spróbuj ponownie.</p>
            <button type="button" class="paragon-modal__cta" data-paragon-finish>ODŚWIEŻ</button>
        </div>`;

    const fetchPartial = async (url) => {
        const res = await fetch(url, { credentials: 'same-origin' }).catch(() => null);
        const html = res && res.ok ? await res.text() : '';
        if (html.trim()) return html;

        // Najczęstszy powód pustki: modal/* zwrócił 404, bo zgłoszenia nie ma już w sesji.
        // Zamiast pustego modala pokazujemy generyczny błąd — a gdy i on nie wstanie, fallback.
        if (!url.startsWith(ENDPOINTS.modalError)) {
            const fallback = await fetch(ENDPOINTS.modalError, { credentials: 'same-origin' }).catch(() => null);
            if (fallback?.ok) {
                const fallbackHtml = await fallback.text();
                if (fallbackHtml.trim()) return fallbackHtml;
            }
        }

        return FALLBACK_ERROR_HTML;
    };

    const post = async (url, body = null, isJson = false) => {
        const opts = { method: 'POST', credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } };
        if (isJson) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
        else if (body) { opts.body = body; }
        return fetch(url, opts);
    };

    const showLoading = () => {
        content.innerHTML = `
            <div class="paragon-modal-step paragon-modal-step--loading" data-step="loading">
                <div class="paragon-modal__progress">
                    <div class="paragon-modal__progress-ring">${LOADER_RING_SVG}</div>
                    <div class="paragon-modal__progress-text" data-paragon-progress>0%</div>
                </div>
                <h2 class="paragon-modal__title">Odczytujemy dane z Twojego<br>dowodu zakupu…</h2>
                <p class="paragon-modal__subtitle">To może chwilę potrwać.</p>
            </div>`;
    };

    const updateProgressText = (val) => {
        const el = content.querySelector('[data-paragon-progress]');
        if (el) el.textContent = `${Math.round(val)}%`;
    };
    const startProgress = () => {
        if (progressTimer) clearInterval(progressTimer);
        progressValue = 0;
        updateProgressText(0);
        progressTimer = setInterval(() => {
            const target = 90;
            progressValue += (target - progressValue) * 0.06;
            if (progressValue > target - 0.5) progressValue = target;
            updateProgressText(progressValue);
        }, 80);
    };
    const stopProgress = async (final = 100) => {
        if (progressTimer) clearInterval(progressTimer);
        progressTimer = null;
        const from = Math.round(progressValue);
        const steps = 5;
        for (let i = 1; i <= steps; i++) {
            await new Promise(r => setTimeout(r, 50));
            updateProgressText(from + (final - from) * (i / steps));
        }
    };

    // Reset strefy uploadu w formularzu, żeby użytkownik mógł wgrać zdjęcie od nowa
    // (po „DODAJ ZDJĘCIE PONOWNIE"). Regenerujemy też klucz idempotencji.
    const resetUploadZone = () => {
        // Wyczyść input i wywołaj `change` — receipt.js przywróci strefę do stanu startowego
        // (podpowiedź zamiast wiersza pliku, zdjęte klasy stanu, usunięty receiptUuid).
        form.querySelectorAll('input[type=file]').forEach(i => {
            i.value = '';
            i.dispatchEvent(new Event('change', { bubbles: true }));
        });
        form.querySelectorAll('input[name=receiptUuid]').forEach(i => i.remove());
        const dropzone = form.querySelector('[data-upload-dropzone]');
        if (dropzone) dropzone.classList.remove('has-file', 'is-uploading', 'is-success', 'is-error');
        const idem = form.querySelector('.idempotency-key-input');
        if (idem && window.crypto?.randomUUID) idem.value = window.crypto.randomUUID();
    };

    const handleAccept = async () => {
        if (!currentUuid) return;
        const res = await post(ENDPOINTS.accept(currentUuid));
        const data = await res.json().catch(() => ({}));
        if (data.html) { setStep(data.html); return; }
        setStep(await fetchPartial(ENDPOINTS.modalError));
    };

    const handleRetry = async () => {
        if (currentUuid) { await post(ENDPOINTS.cancel(currentUuid)); }
        currentUuid = null;
        close();
        resetUploadZone();
    };

    const handleManualStart = async () => { if (currentUuid) setStep(await fetchPartial(ENDPOINTS.modalManualWarning(currentUuid))); };
    const handleManualContinue = async () => { if (currentUuid) setStep(await fetchPartial(ENDPOINTS.modalManualForm(currentUuid))); };
    const handleManualBackToResult = async () => { if (currentUuid) setStep(await fetchPartial(ENDPOINTS.modalResult(currentUuid))); };

    /**
     * Zakres daty zakupu z atrybutów min/max pola (BillDateType). Formularz modala ma `novalidate`,
     * a natywne kalendarze mobilne pozwalają wybrać dzień poza min/max (albo wpisać go z klawiatury),
     * więc zakres pilnujemy sami — inaczej user dowiaduje się o błędzie dopiero z odpowiedzi 422.
     */
    const dateRangeError = (input) => {
        const value = input?.value ?? '';
        if (!value) return null;

        const min = input.getAttribute('min');
        const max = input.getAttribute('max');
        if ((!min || value >= min) && (!max || value <= max)) return null;

        const pretty = (iso) => iso.split('-').reverse().join('.');

        return `Data zakupu musi przypadać w okresie od ${pretty(min)} do ${pretty(max)}.`;
    };

    const showFieldError = (manualForm, field, message) => {
        const el = manualForm.querySelector(`[data-paragon-field-error="${field}"]`);
        if (!el) return;
        el.textContent = message ?? '';
        el.hidden = !message;
    };

    const handleManualSubmit = async (manualForm) => {
        const dateError = dateRangeError(manualForm.elements.date);
        showFieldError(manualForm, 'date', dateError);
        if (dateError) return;

        const payload = {
            number: manualForm.elements.number.value,
            nip: manualForm.elements.nip.value,
            date: manualForm.elements.date.value,
        };
        // Checkbox produktu promocyjnego: gdy zaznaczony, wysyłamy STRING '1' (CheckboxType /
        // BooleanToStringTransformer akceptuje string albo null; bool rzuciłby wyjątek). Gdy
        // odznaczony — pomijamy klucz, wtedy pole dostaje null i mapuje się na false.
        if (manualForm.elements.promotional?.checked) payload.promotional = '1';
        const res = await post(ENDPOINTS.manual(currentUuid), payload, true);
        const data = await res.json().catch(() => ({}));

        if (res.status === 422 && data.errors) {
            manualForm.querySelectorAll('[data-paragon-field-error]').forEach(el => { el.hidden = true; el.textContent = ''; });
            Object.entries(data.errors).forEach(([field, msg]) => showFieldError(manualForm, field, msg));
            return;
        }

        if (res.ok && data.html) { setStep(data.html); return; }
        setStep(await fetchPartial(ENDPOINTS.modalError));
    };

    const handleHelp = async () => setStep(await fetchPartial(ENDPOINTS.modalHelp));
    const handleHelpClose = async () => {
        if (currentUuid) setStep(await fetchPartial(ENDPOINTS.modalResult(currentUuid)));
        else close();
    };

    const handleFinish = () => { close(); window.location.reload(); };

    const bindStepHandlers = () => {
        content.querySelectorAll('[data-paragon-close]').forEach(b => b.addEventListener('click', close));
        content.querySelectorAll('[data-paragon-retry]').forEach(b => b.addEventListener('click', handleRetry));
        content.querySelectorAll('[data-paragon-accept]').forEach(b => b.addEventListener('click', handleAccept));
        content.querySelectorAll('[data-paragon-manual-start]').forEach(b => b.addEventListener('click', handleManualStart));
        content.querySelectorAll('[data-paragon-manual-continue]').forEach(b => b.addEventListener('click', handleManualContinue));
        content.querySelectorAll('[data-paragon-manual-cancel]').forEach(b => b.addEventListener('click', handleManualBackToResult));
        content.querySelectorAll('[data-paragon-manual-back]').forEach(b => b.addEventListener('click', handleManualBackToResult));
        content.querySelectorAll('[data-paragon-help]').forEach(b => b.addEventListener('click', handleHelp));
        content.querySelectorAll('[data-paragon-help-close]').forEach(b => b.addEventListener('click', handleHelpClose));
        content.querySelectorAll('[data-paragon-finish]').forEach(b => b.addEventListener('click', handleFinish));

        const manualForm = content.querySelector('[data-paragon-manual-form]');
        if (manualForm) {
            manualForm.addEventListener('submit', (e) => { e.preventDefault(); handleManualSubmit(manualForm); });

            // Natychmiastowy komunikat po wyborze/wpisaniu daty poza okresem Promocji.
            const dateInput = manualForm.elements.date;
            if (dateInput) {
                const syncDate = () => showFieldError(manualForm, 'date', dateRangeError(dateInput));
                dateInput.addEventListener('change', syncDate);
                dateInput.addEventListener('blur', syncDate);
            }
        }

        // Znacznik „Zakup w sklepie Rossmann" reaguje na wpisany NIP.
        const nipInput = content.querySelector('[data-paragon-nip-input]');
        const rossmannFlag = content.querySelector('[data-paragon-biedronka]');
        if (nipInput && rossmannFlag) {
            const sync = () => {
                const isRossmann = nipInput.value.replace(/\D/g, '') === ROSSMANN_NIP;
                rossmannFlag.classList.toggle('is-yes', isRossmann);
                rossmannFlag.classList.toggle('is-no', !isRossmann);
            };
            nipInput.addEventListener('input', sync);
            sync();
        }
    };

    // Nieudany odczyt przy wyborze pliku (receipt.js) — pokazujemy modal z treścią zależną od tego,
    // ile udało się odczytać. Zdjęcia nie ma jeszcze w formularzu, więc nie ma tu zgłoszenia:
    // „DODAJ ZDJĘCIE PONOWNIE" tylko czyści strefę uploadu.
    document.addEventListener('paragon:scan-failed', async (event) => {
        currentUuid = null;
        open();
        setStep(await fetchPartial(ENDPOINTS.modalScanError(event.detail?.variant ?? 'api_down')));
    });

    // WYŚLIJ w trakcie trwającego odczytu tworzył drugie żądanie równolegle z wolnym POST
    // /api/receipt (paragoniusz potrafi liczyć kilka sekund). Oba żądania czytają sesję na
    // starcie i zapisują ją na końcu, więc wolniejsze nadpisywało nowszy stan — ginęły
    // `paragon_uuids` (własność zgłoszenia → modale 404 → pusty modal) i licznik prób.
    // Capture, żeby wyprzedzić handlery jQuery (validation.js / ajax-form).
    form.addEventListener('submit', (e) => {
        const dropzone = form.querySelector('[data-upload-dropzone]');
        if (dropzone?.classList.contains('is-uploading')) {
            e.preventDefault();
            e.stopImmediatePropagation();
            dropzone.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, true);

    // Podpięcie pod mechanizm `ajax-form` (jQuery). Walidację, spinner na przycisku i POST do
    // /api/application robi ajax-form; my tylko pokazujemy modale na podstawie wyniku.
    const $form = $(form);

    // Loader „Odczytujemy dane" pokazujemy przy submicie. validation.js (needs-validation) na
    // niepoprawnym formularzu robi stopImmediatePropagation, więc ten handler wtedy nie odpali.
    $form.on('submit', () => {
        open();
        showLoading();
        startProgress();
    });

    // Sukces POST /api/application -> {uuid, state}. preventDefault, żeby ajax-form NIE zresetował
    // formularza ani nie przekierował — sterujemy modalami sami.
    $form.on('submit-success', async (e, data) => {
        e.preventDefault();
        await stopProgress(100);
        if (!data || !data.uuid) {
            setStep(await fetchPartial(ENDPOINTS.modalError));
            return;
        }
        currentUuid = data.uuid;
        setStep(await fetchPartial(ENDPOINTS.modalResult(currentUuid)));
    });

    $form.on('submit-error', async (e, xhr) => {
        e.preventDefault();
        await stopProgress(100);

        // Błędy walidacji serwera (np. limit zwrotów na uczestnika: formErrors, albo wyczerpana
        // pula: errors) mają konkretny komunikat — pokazujemy go zamiast generycznego błędu.
        const messages = collectServerMessages(xhr);
        if (messages.length) {
            setStep(await fetchPartial(ENDPOINTS.modalLimit));
            const msgEl = content.querySelector('[data-paragon-message]');
            if (msgEl) msgEl.textContent = messages.join(' ');
            return;
        }

        setStep(await fetchPartial(ENDPOINTS.modalError));
    });
}

// Wyciąga komunikaty walidacji z odpowiedzi ajax-form: formErrors ({pole: [komunikaty]}) oraz
// errors ([komunikaty]) — patrz FormValidationExceptionListener / NoPrizesExceptionListener.
function collectServerMessages(xhr) {
    const json = xhr?.responseJSON || {};
    const out = [];
    if (json.formErrors) {
        Object.values(json.formErrors).forEach((errs) => {
            (Array.isArray(errs) ? errs : [errs]).forEach((m) => { if (m) out.push(m); });
        });
    }
    if (Array.isArray(json.errors)) {
        json.errors.forEach((m) => { if (m) out.push(m); });
    }
    return out;
}
